// What rooms add to the Project menu, and the sheets those items open, so a room's pages bring their own commands
// without editing the shell. Each room's `menu.ts` exports a `RoomMenu`; the shell shows each room's items as a group
// (in room order, after the shell's own) and renders the sheet an item opened with `openRoomSheet(id)`. Sheet ids are
// "<room>/<name>", unique across rooms.
import type { ComponentType } from 'react';
import type { PageProps } from '../shell/ProjectShell.tsx';
import type { MenuEntry } from '../views/Menu.tsx';
import { menu as plan } from './plan/menu.ts';
import { menu as revise } from './revise/menu.ts';

/** What a room's menu items and sheets are given: the page's props, and the shell's commands. */
export interface MenuContext extends PageProps {
  canWrite: boolean;
  canStructure: boolean;
  navigate(href: string): void;
  /** Opens one of the rooms' sheets ("plan/names"). */
  openRoomSheet(id: string): void;
}

export interface RoomMenu {
  /** Project-menu items (a group; separators are added between rooms). */
  items?(ctx: MenuContext): MenuEntry[];
  /** Sheets by id ("<room>/<name>"), each given the context and a way to close. */
  sheets?: Record<string, ComponentType<MenuContext & { onClose(): void }>>;
}

export const ROOM_MENUS: readonly RoomMenu[] = [plan, revise];

/** Every room's items, grouped with separators between them. */
export function roomMenuEntries(ctx: MenuContext): MenuEntry[] {
  const out: MenuEntry[] = [];
  for (const m of ROOM_MENUS) {
    const items = m.items?.(ctx) ?? [];
    if (items.length) out.push({ kind: 'separator' }, ...items);
  }
  return out;
}

/** The sheet component for an id, if a room has one. */
export function roomSheet(id: string): RoomMenu['sheets'] extends infer S ? S extends Record<string, infer C> ? C | undefined : never : never {
  for (const m of ROOM_MENUS) {
    const c = m.sheets?.[id];
    if (c) return c;
  }
  return undefined;
}
